import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ApiContext } from '@api/api';
import {
  CheckDuplicationExpeditionResponseOutDTO,
  ComputeReservationDurationConfiguration,
  DuplicationLocationOutDTO,
  DuplicationMoreReservationTimeslotsDTO,
  FreeSlotsWrapperOutDTO,
  MultiduplicationV3JobItemOutDTO,
  OpeningHourStateEnum,
  ReservationStateEnum,
  TimeslotCalendarDTO,
} from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { Link } from 'react-router-dom';
import useNavigation from '@hooks/useNavigation.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import COText from '@app/pages/COText/COText.tsx';
import Panel from '@components/ringil3/Panel/Panel.tsx';
import COSelect from '@components/ringil3/COSelect/COSelect.tsx';
import { ClickableCursor } from '@components/ringil3/styles.tsx';
import { ColWithGap } from '@components/styles.tsx';
import { VerticalFieldAndValue } from '@components/ringil3/Containers/VerticalFieldAndValue.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import InputCompanyRamp from '@components/ringil3/Input/InputCompanyRamp/InputCompanyRamp.tsx';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix.tsx';
import InputHourManual from '@components/ringil3/Input/InputHour/InputHourManual.tsx';
import SvgAlert from '@icons/alert-circle.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import SvgOK from '@icons/check-circle.svg?react';
import SvgCalendar from '@components/framework/icons/Calendar.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import SvgReset from '@icons/reset-filled.svg?react';
import SvgIconFile from '@components/framework/icons/IconFile.tsx';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import {
  apiUtcFromDayjs,
  dayjsFromApiUtcInTz,
  dayjsFromDayAndTimeStrings,
  formatDateFromStringWithLocale,
  formatTimeIntervalFromStringInTzWithLocale,
  stringHHmmFromStringInTz,
} from '@utils/dateWithTz.tsx';
import { isNotNullOrUndefined, isNullOrUndefined, parseNumberOrUndefined } from '@utils/utils.tsx';
import NoAvailableSlotsMessage from '@components/RingilComponents/NoAvailableSlots/NoAvailableSlotsMessage.tsx';
import { remapOpeningHours } from '@components/ringil3/Features/Expedition/DuplicateExpeditionSidebar/common.ts';

/** Codes for the Lokace segmented filter; the state behind it is the boolean `onlyErrors`. */
const LOCATION_FILTER = { ALL: 'ALL', ONLY_ERRORS: 'ONLY_ERRORS' } as const;

interface ComponentProps {
  jobId: string;
  item: MultiduplicationV3JobItemOutDTO;
  /** Editor (timeslot change) is offered only when true; otherwise the breakdown is read-only. */
  canEdit: boolean;
  onSaved: () => void;
}

/**
 * "Lokace" expansion of one row: per location an "Otevírací doba" status row (collision -> the
 * planned-vs-opening comparison) and a "Rezervace" status row (occupied -> strip + inline timeslot
 * editor). Data source by state: stored check (draft), live sync check (failed rows post-execution,
 * where the stored check is gone), or the execution's created-reservations (successful rows).
 */
const MultiduplicationV3RowDetail: React.FC<ComponentProps> = ({ jobId, item, canEdit, onSaved }) => {
  const {
    getMultiduplicationV3ItemDetail,
    updateMultiduplicationV3Item,
    checkDuplicationExpedition,
    getFreeSlotsForLocationReservation,
    getCreateEditReservationSettings,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [onlyErrors, setOnlyErrors] = useState(false);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const nav = useNavigation();
  const [check, setCheck] = useState<CheckDuplicationExpeditionResponseOutDTO>();
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState<Map<string, DuplicationMoreReservationTimeslotsDTO>>(new Map());
  const [slots, setSlots] = useState<Map<string, FreeSlotsWrapperOutDTO[]>>(new Map());
  const [validReservation, setValidReservation] = useState<Map<string, boolean>>(new Map());
  const [settingsMap, setSettingsMap] = useState<Map<string, ComputeReservationDurationConfiguration>>(new Map());
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const recheckTimer = useRef<number | null>(null);
  const durationRefreshTimer = useRef<number | null>(null);

  // Successful executed rows carry their result inline — no check to load
  const executionMode = item.execution != null && isNotNullOrUndefined(item.execution.expeditionId);

  // Per-location key: one duplication per location in a v3 item (one target date)
  const keyOf = (loc: DuplicationLocationOutDTO) => loc.locationId;

  /**
   * States in which the duplicated date cannot keep its reservation and the user has to pick a new
   * slot: the ramp is occupied (FULL), or the location was replaced by another one and the original
   * ramp does not exist there any more (LOCATION_SUPERSEDED).
   */
  const needsNewTimeslot = (state?: ReservationStateEnum) =>
    state === ReservationStateEnum.FULL || state === ReservationStateEnum.LOCATION_SUPERSEDED;

  const updateValidReservation = (locations: DuplicationLocationOutDTO[]) => {
    const newMap = new Map<string, boolean>();
    locations?.forEach(loc => {
      const dupl = loc.duplications?.[0];
      if (dupl == null) return;
      if (dupl.reservationState === ReservationStateEnum.FREE || dupl.reservationState === ReservationStateEnum.FREE_NOSLOT) {
        newMap.set(keyOf(loc), true);
      } else if (needsNewTimeslot(dupl.reservationState)) {
        newMap.set(keyOf(loc), false);
      }
    });
    setValidReservation(newMap);
  };

  const loadReservationSettings = (locations: DuplicationLocationOutDTO[]) => {
    const fullOwners = locations
      ?.flatMap(loc => loc.duplications.filter(dupl => needsNewTimeslot(dupl.reservationState)).map(() => loc.companyLocationOwnerId))
      .filter((v, i, a) => v && a.indexOf(v) === i);
    fullOwners
      ?.filter(ownerId => !settingsMap.has(ownerId))
      .forEach(ownerId => {
        getCreateEditReservationSettings(ownerId, s => {
          setSettingsMap(prev => new Map(prev).set(ownerId, s));
        });
      });
  };

  const applyCheck = (d: CheckDuplicationExpeditionResponseOutDTO) => {
    setCheck(d);
    updateValidReservation(d.locations);
    loadReservationSettings(d.locations);
  };

  const runCheck = (current: Map<string, DuplicationMoreReservationTimeslotsDTO>) => {
    if (item.targetDate == null) return;
    setChecking(true);
    checkDuplicationExpedition(
      {
        fromExpeditionId: item.expeditionId,
        duplicateReservations: true,
        copyAttachments: false,
        copyToLocalDates: [item.targetDate],
        newTimeslots: [...current.values()],
      },
      d => {
        setChecking(false);
        setLoading(false);
        applyCheck(d);
      },
      null,
      { onError: () => setChecking(false) },
    );
  };

  // Initial load: stored check + stored overrides; failed rows post-execution have no stored
  // check (resultJson holds the execution error) -> build it via the live sync check.
  useEffect(() => {
    if (executionMode) {
      setLoading(false);
      return;
    }
    getMultiduplicationV3ItemDetail(jobId, item.itemId, d => {
      const map = new Map<string, DuplicationMoreReservationTimeslotsDTO>();
      (d.config?.newTimeslots ?? []).forEach(t => map.set(t.locationId, t));
      setOverrides(map);
      if (d.check != null) {
        applyCheck(d.check);
        setLoading(false);
      } else if (item.targetDate != null) {
        runCheck(map);
      } else {
        setLoading(false);
      }
    });
  }, [jobId, item.itemId]);

  const setOverride = (key: string, override: DuplicationMoreReservationTimeslotsDTO | null, recheck: boolean = true) => {
    setOverrides(prev => {
      const next = new Map(prev);
      if (override == null) next.delete(key);
      else next.set(key, override);
      if (recheck) {
        if (recheckTimer.current != null) window.clearTimeout(recheckTimer.current);
        recheckTimer.current = window.setTimeout(() => runCheck(next), 800);
      }
      return next;
    });
  };

  const offerAvailableSlots = (key: string, localDate: string, durationInMinutes: number, companyLocationId: string, locationCustomerId: string) => {
    // Occupied reservations can miss their time detail — never send NaN/undefined to the free-slots EP (Spring 500s)
    if (!Number.isFinite(durationInMinutes) || durationInMinutes <= 0 || localDate == null) return;
    getFreeSlotsForLocationReservation(
      localDate,
      durationInMinutes,
      companyLocationId,
      [],
      {
        customerId: locationCustomerId,
        rampId: null,
        locationId: companyLocationId,
        labelIds: [],
        openGate: false,
        createExpedition: false,
        sendNotification: true,
      } as any,
      data => setSlots(prev => new Map(prev).set(key, data)),
    );
  };

  const applySlot = (key: string, slot: TimeslotCalendarDTO, rampId: string) => {
    const current = overrides.get(key);
    const slotTz = isNotNullOrUndefined(slot.utcStart) && isNotNullOrUndefined(slot.timezoneId) ? slot.timezoneId : 'Europe/Prague';
    const slotSince = isNotNullOrUndefined(slot.utcStart) && isNotNullOrUndefined(slot.timezoneId) ? slot.utcStart : slot.since;
    const utcStart = isNotNullOrUndefined(slot.utcStart) ? slot.utcStart : null;
    const utcEnd =
      utcStart && current?.durationInMinutes
        ? apiUtcFromDayjs(dayjsFromApiUtcInTz(utcStart, slotTz).add(current.durationInMinutes, 'minute'))
        : isNotNullOrUndefined(slot.utcEnd)
          ? slot.utcEnd
          : null;
    setOverride(key, {
      ...current,
      sinceHourString: stringHHmmFromStringInTz(slotSince, slotTz),
      utcStart,
      utcEnd,
      timezoneId: isNotNullOrUndefined(slot.timezoneId) ? slot.timezoneId : null,
      rampId,
    } as DuplicationMoreReservationTimeslotsDTO);
  };

  const canEditDuration = (locationOwnerId: string) => {
    const isOwner = loggedUser.companyId === locationOwnerId;
    const settings = settingsMap.get(locationOwnerId);
    return isOwner ? settings?.editDuration === true : settings?.editDurationCounterParty === true;
  };

  const save = () => {
    setSaving(true);
    updateMultiduplicationV3Item(
      jobId,
      item.itemId,
      { newTimeslots: [...overrides.values()] },
      () => {
        setSaving(false);
        onSaved();
      },
      null,
      { onError: () => setSaving(false) },
    );
  };

  const startOverride = (loc: DuplicationLocationOutDTO, key: string) => {
    const dupl = loc.duplications?.[0];
    const detail = dupl?.reservationFullDetail;
    const hasNew = isNotNullOrUndefined(detail?.utcStart) && isNotNullOrUndefined(detail?.utcEnd) && isNotNullOrUndefined(detail?.timezoneId);
    const tz = hasNew ? detail.timezoneId : 'Europe/Prague';
    const sinceDayjs = dayjsFromApiUtcInTz(hasNew ? detail.utcStart : detail?.since, tz);
    const tillDayjs = dayjsFromApiUtcInTz(hasNew ? detail.utcEnd : detail?.till, tz);
    // Missing/invalid time detail on the occupied reservation yields NaN — fall back to 30 min
    const rawDuration = tillDayjs.diff(sinceDayjs, 'minute');
    const durationInMinutes = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : 30;
    // When UTC fields are present derive the local date from utcStart+tz,
    // not from the legacy localDate field which still holds the original expedition date.
    const targetLocalDate = (hasNew ? sinceDayjs.format('YYYY-MM-DD') : detail?.localDate) ?? item.targetDate;
    const targetDate = dupl.deliverySlots?.dayWithInterval?.find(d => d.localDate === targetLocalDate)?.localDate || targetLocalDate;
    setOverride(
      key,
      {
        locationId: loc.locationId,
        sinceHourString: null,
        durationInMinutes,
        localDate: targetLocalDate,
        rampId: detail?.rampId,
        id: dupl.id,
        // utcStart / utcEnd intentionally absent — set by applySlot when user picks a slot
        timezoneId: hasNew ? detail.timezoneId : null,
      } as DuplicationMoreReservationTimeslotsDTO,
      false,
    );
    offerAvailableSlots(key, targetDate, durationInMinutes, loc.companyLocationId, loc.companyLocationOwnerId);
  };

  const occupiedDetailText = (loc: DuplicationLocationOutDTO) => {
    const detail = loc.duplications?.[0]?.reservationFullDetail;
    const hasNew = isNotNullOrUndefined(detail?.utcStart) && isNotNullOrUndefined(detail?.utcEnd) && isNotNullOrUndefined(detail?.timezoneId);
    const tz = hasNew ? detail.timezoneId : 'Europe/Prague';
    const since = hasNew ? detail.utcStart : detail?.since;
    const till = hasNew ? detail.utcEnd : detail?.till;
    return tr('DuplicateExpeditionSidebar.timeslotFullRampNameAndTime', ' {{rampName}}, {{since}} - {{till}}', {
      rampName: detail?.rampName,
      since: stringHHmmFromStringInTz(since, tz),
      till: stringHHmmFromStringInTz(till, tz),
    });
  };

  /**
   * The filter is a segmented selector, not a pair of buttons: InputSelect/SelectableButton already
   * renders the design's CO Select exactly — brandMedium fill with an orange border and a check on
   * the selected side, quaternary fill on the other.
   */
  const headerBar = (withToggle: boolean) => (
    <div className={'flex flex-row justify-between items-center rounded-t-md bg-content-quaternary px-6 py-4'}>
      <span className={'font-medium text-content-primary'}>{tr('MultiduplicationV3.locations', 'Lokace')}</span>
      {withToggle && (
        <InputSelect
          selectedItemCode={onlyErrors ? LOCATION_FILTER.ONLY_ERRORS : LOCATION_FILTER.ALL}
          model={[
            { code: LOCATION_FILTER.ALL, label: tr('MultiduplicationV3.expansionAll', 'Vše') },
            { code: LOCATION_FILTER.ONLY_ERRORS, label: tr('MultiduplicationV3.expansionOnlyErrors', 'Jen chyby') },
          ]}
          onSelect={code => setOnlyErrors(code === LOCATION_FILTER.ONLY_ERRORS)}
          dataTestIdPrefix={'multiduplication-v3-location-filter'}
        />
      )}
    </div>
  );

  /** Label column is a fixed 201px in the design, so the value columns line up down the whole block. */
  const labeledRow = (icon: React.ReactNode, label: string, content: React.ReactNode) => (
    <div className={'flex flex-row items-start w-full'}>
      <div className={'flex w-[201px] shrink-0 flex-row gap-2 items-center py-2 font-medium text-content-primary'}>
        {icon}
        <COText label={label} />
      </div>
      <div className={'flex min-w-px flex-1 flex-col gap-2 py-2 text-content-secondary'}>{content}</div>
    </div>
  );

  // ---------- Successful executed rows: reservations from the execution result ----------
  if (executionMode) {
    const locations = (item.execution?.locations ?? []).filter(l => !onlyErrors || l.reservationCreated === false);
    return (
      <div className={'flex flex-col border border-solid border-base-quaternary rounded-md overflow-hidden'}>
        {headerBar(true)}
        {locations.map((loc, i) => (
          <div key={i} className={clsx('flex flex-row items-start py-2', i % 2 === 1 && 'bg-base-quaternary')}>
            <div className={'w-[250px] shrink-0 p-4 text-content-primary'}>{loc.locationName}</div>
            <div className={'flex min-w-px flex-1 flex-col justify-center'}>
              {loc.reservationCreated != null ? (
                labeledRow(
                  <SvgCalendar />,
                  tr('MultiduplicationV3.reservation', 'Rezervace'),
                  loc.reservationCreated === true && isNotNullOrUndefined(loc.reservationId) ? (
                    <span className={'flex flex-row gap-2 items-center'}>
                      <SvgOK className={'text-complementary-success shrink-0'} />
                      <Link to={nav.createNavigationLink(nav.urlFunctions.createReservationDetail(loc.reservationId))}>{loc.reservationApplicationId}</Link>
                      {loc.hasNoSlots === true && (
                        <span title={tr('DuplicateExpeditionSidebar.reservationWithoutTimeSlot', 'The reservation does not have a time slot')}>
                          <SvgWarning className={'text-complementary-warning'} />
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className={'flex flex-row gap-1 items-center text-complementary-error'}>
                      <SvgAlert className={'shrink-0'} />
                      {tr('MultiduplicationV3.notCreated', 'Nevytvořeno')}
                    </span>
                  ),
                )
              ) : (
                <span className={'text-content-tertiary'}>—</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---------- Check-driven (draft rows + failed rows on the finish page) ----------
  if (loading) {
    return <SvgLoader className={'animate-spin'} />;
  }
  if (check == null) {
    return null;
  }

  const locationHasIssue = (loc: DuplicationLocationOutDTO) => {
    const dupl = loc.duplications?.[0];
    if (dupl == null) return false;
    const openingIssue =
      dupl.openingHourState === OpeningHourStateEnum.COLLISION ||
      dupl.openingHourState === OpeningHourStateEnum.CLOSED_EXCEPTION ||
      (dupl.deliverySlots?.dayWithInterval ?? []).some(d => d.isCollision);
    return openingIssue || needsNewTimeslot(dupl.reservationState);
  };

  const visibleLocations = check.locations.filter(loc => !onlyErrors || locationHasIssue(loc) || overrides.has(keyOf(loc)));

  return (
    <div className={'flex flex-col border border-solid border-base-quaternary rounded-md overflow-hidden'}>
      {headerBar(true)}

      {visibleLocations.map((loc, locIndex) => {
        const dupl = loc.duplications?.[0];
        if (dupl == null) return null;
        const key = keyOf(loc);
        const override = overrides.get(key);
        const locSlots = slots.get(key);
        const occupied = dupl.reservationState === ReservationStateEnum.FULL;
        // The location was replaced; nothing is occupied, the original ramp simply is not here any more.
        const superseded = dupl.reservationState === ReservationStateEnum.LOCATION_SUPERSEDED;
        const slotDays = dupl.deliverySlots?.dayWithInterval ?? [];
        const hasCollision =
          dupl.openingHourState === OpeningHourStateEnum.COLLISION ||
          dupl.openingHourState === OpeningHourStateEnum.CLOSED_EXCEPTION ||
          slotDays.some(d => d.isCollision);
        const slotDates = slotDays.map(d => d.localDate).filter(isNotNullOrUndefined);
        const dateRange =
          slotDates.length === 0
            ? null
            : slotDates[0] === slotDates[slotDates.length - 1]
              ? formatDateFromStringWithLocale(slotDates[0])
              : `${formatDateFromStringWithLocale(slotDates[0])} – ${formatDateFromStringWithLocale(slotDates[slotDates.length - 1])}`;

        const deliveryRows = slotDays.reduce((previousValue, currentValue) => {
          const newValue = currentValue.intervals.map(i => ({
            localDate: currentValue.localDate,
            hours: `${i.sinceHoursString} - ${i.tillHoursString}`,
            isCollision: currentValue.isCollision,
          }));
          const newValues =
            newValue.length > 0
              ? newValue
              : [{ localDate: currentValue.localDate, hours: tr(`LocalityDetail.allDay`, `non-stop`), isCollision: currentValue.isCollision }];
          return [...previousValue, ...newValues];
        }, []);

        return (
          <div key={loc.locationId} className={clsx('flex flex-row items-start py-2', locIndex % 2 === 1 && 'bg-base-quaternary')}>
            <div className={'flex w-[250px] shrink-0 flex-col gap-1 p-4'}>
              <span className={'text-content-primary'}>{loc.locationName ?? loc.city}</span>
              {/* Both names are sent only when the stop's location resolved to a replacement. */}
              {isNotNullOrUndefined(loc.supersededByLocationName) && (
                <span className={'text-sm text-complementary-warning'}>
                  {tr('DuplicateExpeditionSidebar.locationSupersededMarker', 'was {{oldName}}, now {{newName}}', {
                    oldName: loc.supersededFromLocationName,
                    newName: loc.supersededByLocationName,
                  })}
                </span>
              )}
            </div>
            <div className={'flex min-w-px flex-1 flex-col justify-center'}>
              {labeledRow(
                <SvgCalendar />,
                tr('MultiduplicationV3.openingHours', 'Otevírací doba'),
                <>
                  {hasCollision ? (
                    <span className={'flex flex-row gap-1 items-center'}>
                      <SvgWarning className={'text-complementary-warning shrink-0'} />
                      <COText label={tr('MultiduplicationV3.collision', 'Kolize')} />
                      {dateRange != null && <span className={'text-content-tertiary'}>{dateRange}</span>}
                    </span>
                  ) : (
                    <span className={'flex flex-row gap-1 items-center'}>
                      <SvgOK className={'text-complementary-success shrink-0'} />
                      <span>{dateRange ?? tr('MultiduplicationV3.allOk', 'Vše v pořádku')}</span>
                    </span>
                  )}

                  {hasCollision && (
                    <div className={'grid grid-cols-2 gap-y-2 mt-1'}>
                      <COText label={tr('MultiduplicationV3.plannedArrival', 'Naplánovaný příjezd')} className={'text-xs text-content-secondary'} />
                      <COText label={tr('MultiduplicationV3.locationOpeningHours', 'Otevírací doba lokace')} className={'text-xs text-content-secondary'} />
                      <div>
                        {deliveryRows.map((ds, dsIndex) => (
                          <Fragment key={dsIndex}>
                            <div className={'flex flex-row gap-2'}>
                              {ds.isCollision ? <SvgAlert className={'text-complementary-error shrink-0'} /> : <SvgOK className={'text-complementary-success shrink-0'} />}
                              <div
                                className={clsx({ 'text-complementary-error': ds.isCollision })}
                                data-testid={`multiduplication-collision-location-${locIndex}-slot-${dsIndex}`}
                              >
                                {formatDateFromStringWithLocale(ds.localDate)} | {ds.hours}
                              </div>
                            </div>
                          </Fragment>
                        ))}
                      </div>
                      <div>
                        {isNotNullOrUndefined(dupl.openingHours) &&
                          remapOpeningHours(dupl.openingHours, tr).map((ds, dsIndex) => (
                            <div key={dsIndex} data-testid={`multiduplication-collision-location-${locIndex}-openingHour-${dsIndex}`}>
                              {formatDateFromStringWithLocale(ds.localDate)} | {ds.hours}
                            </div>
                          ))}
                        {isNullOrUndefined(dupl.openingHours) && (
                          <div className={'flex flex-row gap-2 text-content-tertiary items-center'}>
                            <SvgIconFile fill={'var(--ringil-tertiary-color)'} />
                            <COText label={tr('DuplicateExpeditionSidebar.notSpecified', 'Not specified')} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {isNotNullOrUndefined(dupl.exceptionPresent) && (
                    <div className={'text-sm text-content-tertiary'}>
                      {tr(
                        'MultiduplicationV3.exceptionNote',
                        'Otevírací doba je ovlivněna výjimkou – {{exceptionName}}. Pro více informací kontaktujte vlastníka lokace.',
                        { exceptionName: dupl.exceptionPresent.exceptionName },
                      )}
                    </div>
                  )}
                </>,
              )}

              {(dupl.reservationState != null || override != null) &&
                labeledRow(
                  <SvgCalendar />,
                  tr('MultiduplicationV3.reservation', 'Rezervace'),
                  <>
                    {!occupied && !superseded && (
                      <span className={'flex flex-row gap-1 items-center'}>
                        <SvgOK className={'text-complementary-success shrink-0'} />
                        <span>{dateRange ?? tr('MultiduplicationV3.allOk', 'Vše v pořádku')}</span>
                      </span>
                    )}
                    {superseded && (
                      <span className={'flex flex-row gap-1 items-center text-complementary-warning font-medium'}>
                        <SvgWarning className={'shrink-0'} />
                        {tr('DuplicateExpeditionSidebar.locationSuperseded', 'Location replaced')}
                      </span>
                    )}
                    {occupied && (
                      <span className={'flex flex-row gap-1 items-center text-complementary-error font-medium'}>
                        <SvgAlert className={'shrink-0'} />
                        {tr('MultiduplicationV3.occupied', 'Obsazeno')} (1)
                      </span>
                    )}

                    {occupied && (
                      <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
                        <div className="flex flex-row items-center justify-between gap-2">
                          <div>
                            <span>{tr('DuplicateExpeditionSidebar.timeslotFull', 'Termín timeslotu je obsazený:')}</span>
                            <span className="font-bold">{occupiedDetailText(loc)}</span>
                          </div>
                          {override == null && canEdit && (
                            <RingilButton
                              mode={'table'}
                              label={tr('MultiduplicationV3.offerFreeSlots', 'Nabídnout volné termíny')}
                              onClick={() => startOverride(loc, key)}
                            />
                          )}
                        </div>
                      </COInlineMessage>
                    )}

                    {/*
                      reservationFullDetail is deliberately not used here (and is null anyway) — it describes
                      the *old* location's ramp, which is in a different warehouse.
                    */}
                    {superseded && (
                      <COInlineMessage variant={'warning'} icon={<SvgWarning className="text-content-tertiary" />}>
                        <div className="flex flex-row items-center justify-between gap-2">
                          <div>
                            {tr(
                              'DuplicateExpeditionSidebar.locationSupersededMessage',
                              'This location was replaced by {{newLocationName}}. The original ramp no longer exists here — choose a new time slot.',
                              { newLocationName: loc.supersededByLocationName ?? loc.locationName },
                            )}
                          </div>
                          {override == null && canEdit && (
                            <RingilButton
                              mode={'table'}
                              label={tr('MultiduplicationV3.offerFreeSlots', 'Nabídnout volné termíny')}
                              onClick={() => startOverride(loc, key)}
                            />
                          )}
                        </div>
                      </COInlineMessage>
                    )}

                    {override != null && canEdit && (
                      <div className="p-4 bg-content-quaternary">
                        <COHeadline variant={'h3'} title={tr('MultiduplicationV3.changeTimeslot', 'Změnit timeslot')}></COHeadline>

                        {locSlots != null && (
                          <Panel headingText={tr('MultiduplicationV3.freeSlots', 'Volné termíny')} icon={<SvgCalendar />}>
                            <ColWithGap>
                              {locSlots[0]?.freeSlots.map((s, i) => (
                                <VerticalFieldAndValue label={s.name} labelMode={'normal'} key={i}>
                                  {s.slots.length == 0 && <NoAvailableSlotsMessage unavailability={s.unavailability} />}
                                  <div className="flex flex-wrap gap-2">
                                    {s.slots.map((slot, j) => (
                                      <ClickableCursor key={j} onClick={() => applySlot(key, slot, s.rampId)}>
                                        <COSelect
                                          text={(() => {
                                            const hasNew =
                                              isNotNullOrUndefined(slot.utcStart) && isNotNullOrUndefined(slot.utcEnd) && isNotNullOrUndefined(slot.timezoneId);
                                            const tz = hasNew ? slot.timezoneId : 'Europe/Prague';
                                            const since = hasNew ? slot.utcStart : slot.since;
                                            const till = hasNew ? slot.utcEnd : slot.till;
                                            return formatTimeIntervalFromStringInTzWithLocale(false, since, till, tz, tr);
                                          })()}
                                        />
                                      </ClickableCursor>
                                    ))}
                                  </div>
                                </VerticalFieldAndValue>
                              ))}
                            </ColWithGap>
                          </Panel>
                        )}

                        <div className="w-full grid grid-cols-2 gap-4">
                          <VerticalFieldAndValue label={tr(`CreateTimeslotSidebar.ramp`, `Rampa`)} labelMode={'normal'}>
                            <InputCompanyRamp
                              value={override.rampId}
                              onChange={v => setOverride(key, { ...override, rampId: v })}
                              companyLocationId={loc.companyLocationId}
                              companyId={loc.companyLocationOwnerId}
                              showTemporaryClosed={false}
                            />
                          </VerticalFieldAndValue>
                          <VerticalFieldAndValue label={tr(`CreateTimeslotSidebar.duration`, `Trvání`)} labelMode={'normal'}>
                            <InputTextWithSuffix
                              suffix={'min'}
                              value={override.durationInMinutes}
                              onChange={v => {
                                const parsedDuration = parseNumberOrUndefined(v);
                                const utcEnd =
                                  override.utcStart && override.timezoneId && parsedDuration
                                    ? apiUtcFromDayjs(dayjsFromApiUtcInTz(override.utcStart, override.timezoneId).add(parsedDuration, 'minute'))
                                    : override.utcEnd ?? null;
                                setOverride(key, { ...override, durationInMinutes: parsedDuration, utcEnd });
                                if (parsedDuration >= 10 && override.localDate) {
                                  if (durationRefreshTimer.current != null) window.clearTimeout(durationRefreshTimer.current);
                                  durationRefreshTimer.current = window.setTimeout(() => {
                                    offerAvailableSlots(key, override.localDate, parsedDuration, loc.companyLocationId, loc.companyLocationOwnerId);
                                  }, 800);
                                }
                              }}
                              readOnly={!canEditDuration(loc.companyLocationOwnerId)}
                            />
                          </VerticalFieldAndValue>
                        </div>
                        <VerticalFieldAndValue label={tr(`CreateTimeslotSidebar.arrival`, `Příjezd`)} labelMode={'normal'}>
                          <InputHourManual
                            value={override.sinceHourString ?? ''}
                            onChange={v => {
                              const tz = override.timezoneId;
                              const utcStartDayjs = tz && override.localDate && v ? dayjsFromDayAndTimeStrings(override.localDate, v, tz) : null;
                              const utcStart = utcStartDayjs ? apiUtcFromDayjs(utcStartDayjs) : null;
                              const utcEnd =
                                utcStartDayjs && override.durationInMinutes ? apiUtcFromDayjs(utcStartDayjs.add(override.durationInMinutes, 'minute')) : null;
                              setOverride(key, { ...override, sinceHourString: v, utcStart, utcEnd });
                            }}
                          />
                        </VerticalFieldAndValue>
                        {validReservation.get(key) && (
                          <COInlineMessage variant={'success'}>
                            {tr(`DuplicateExpeditionSidebar.reservationPossible`, `Reservation is possible in this time and ramp`)}
                          </COInlineMessage>
                        )}
                        {validReservation.get(key) == false && (
                          <COInlineMessage variant={'error'}>
                            {tr(`DuplicateExpeditionSidebar.reservationImpossible`, `Reservation is not possible in this time and ramp`)}
                          </COInlineMessage>
                        )}
                        <div className="mt-2 flex flex-row justify-between items-center">
                          <RingilButton
                            mode={'text-only'}
                            icon={<SvgReset />}
                            label={tr('MultiduplicationV3.checkOccupancy', 'Zkontrolovat obsazenost')}
                            onClick={() => runCheck(overrides)}
                          />
                          <div className="flex flex-row gap-2 items-center">
                            {checking && <SvgLoader className={'animate-spin'} />}
                            <RingilButton
                              mode={'text-only'}
                              label={tr('DuplicateExpeditionSidebar.cancelNewTimeslot', 'Zrušit')}
                              onClick={() => {
                                setOverride(key, null);
                                setSlots(prev => {
                                  const next = new Map(prev);
                                  next.delete(key);
                                  return next;
                                });
                              }}
                            />
                            <RingilButton
                              mode={'filled-rectangle'}
                              label={tr('DuplicateExpeditionSidebar.saveNewTimeslot', 'Uložit')}
                              onClick={save}
                              loading={saving}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>,
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MultiduplicationV3RowDetail;
